import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import {
  GetUsersByPractitionerDocument,
  GetUsersByPractitionerQuery,
  GetUsersByPractitionerQueryVariables,
} from "../generated/graphql"
import {
  AccountDeleted,
  ClientDisconnected,
  Declined,
  InviteCodeStatus,
  Pending,
  PractitionerDisconnected,
} from "../utils/constants"
import { useGraphQLClient } from "./useGraphQLClient"
import { useUser } from "./useUser"

export function useClients(id: string = "") {
  const { user } = useUser()
  const graphQLClient = useGraphQLClient()

  // Use React Query for better caching and automatic refetching
  const {
    data: clientsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["GetUsersByPractitioner", id || user.id],
    queryFn: async () => {
      return await graphQLClient.request<
        GetUsersByPractitionerQuery,
        GetUsersByPractitionerQueryVariables
      >(GetUsersByPractitionerDocument, {
        practitioner_id: id ? id : user.id,
      })
    },
    enabled: id !== "" || !!user.id,
  })

  /**
   * Sets metadata and all the latest all scored assessments for every client connected to this practitioner
   */
  const { clients, uniqueQuestionnaires } = useMemo(() => {
    const uniqueQuestionnaires =
      clientsData?.users.reduce((acc: string[], user) => {
        user.mostRecentScoredQuestionnaires.forEach((quest) => {
          if (
            quest.questionnaire_name &&
            !acc.includes(quest.questionnaire_name)
          ) {
            acc.push(quest.questionnaire_name)
          }
        })
        return acc
      }, []) ?? []
    const clients = clientsData?.users.map((user) => {
      const firstName =
        user.client_demographics?.[0]?.first_name ?? user.first_name
      const lastName =
        user.client_demographics?.[0]?.last_name ?? user.last_name
      const displayName = user.display_name
      const inviteStatus = user.client_invite_codes[0].status
      const pending = inviteStatus === InviteCodeStatus.PENDING
      const declined = inviteStatus === InviteCodeStatus.DECLINED
      const practitionerDisconnected =
        inviteStatus === InviteCodeStatus.PRACTITIONER_DISCONNECTED
      const clientDisconnected =
        inviteStatus === InviteCodeStatus.CLIENT_DISCONNECTED
      const connected = inviteStatus === InviteCodeStatus.CONNECTED
      const accountDeleted = inviteStatus === InviteCodeStatus.ACCOUNT_DELETED
      // manipulate the questionnaire to be in the format we want
      const questionnaires = user.mostRecentScoredQuestionnaires.map(
        (quest) => {
          return {
            sortData: connected ? quest.display_score : "",
            data: connected ? quest : "",
          }
        }
      )
      return {
        id: {
          sortData: user.id,
          data: user.id,
        },
        lastName: {
          sortData: pending
            ? `00000${lastName}`
            : declined
            ? `zzzzz${lastName}`
            : practitionerDisconnected
            ? `zzzzzz${lastName}`
            : clientDisconnected
            ? `zzzzzzz${lastName}`
            : lastName,
          data: lastName,
        },
        firstName: {
          sortData: firstName,
          data: firstName,
        },
        displayName: {
          sortData: displayName,
          data: displayName,
        },
        dailyCheckins: {
          sortData: pending
            ? Pending
            : declined
            ? Declined
            : clientDisconnected
            ? ClientDisconnected
            : practitionerDisconnected
            ? PractitionerDisconnected
            : accountDeleted
            ? AccountDeleted
            : user.mood_measurements_aggregate.aggregate?.count ?? 0,
          data: pending
            ? Pending
            : declined
            ? Declined
            : clientDisconnected
            ? ClientDisconnected
            : practitionerDisconnected
            ? PractitionerDisconnected
            : accountDeleted
            ? AccountDeleted
            : user.mood_measurements_aggregate.aggregate?.count ?? 0,
        },
        mobileSensingIndex: {
          sortData: connected ? 1 : "",
          data: connected ? 1 : "",
        },
        questionnaires: {
          sortData: "",
          data: questionnaires,
        },
        more: {
          sortData: "",
          data: {
            code: user.client_invite_codes[0].code, // it can be assumed that if we get data back this will have one element
            status: user.client_invite_codes[0].status,
            ended_at: user.client_invite_codes[0].ended_at,
            created_at: user.client_invite_codes[0].created_at,
          },
        },
      }
    })
    return { clients, uniqueQuestionnaires }
  }, [clientsData])

  return { clients, uniqueQuestionnaires }
}
